import { moneyToCents, roundMoney } from './money-rounding';
import { getResidentialMpirAnnualRateDecimal } from './legislative';
import { MPIR_PCT } from './regulatory-rates';

export type ResidentialAccommodationPaymentType = 'RAD' | 'DAP' | 'COMBINATION';

export interface DailyAccommodationPaymentInput {
  agreedRoomPrice: number;
  radPaid?: number | null;
  /** Section 308 deductions only; add back before computing the unpaid share. */
  retentionDeductions?: number | null;
  /** Tenant-local agreement date for legacy callers without an explicit catalogue rate. */
  priceAgreementDate?: string | null;
  mpirPct?: number | null;
}

export interface AccommodationAgreementBreakdownInput {
  paymentType: ResidentialAccommodationPaymentType;
  agreedRoomPrice: number;
  radAmount?: number | null;
  combinationRadPortion?: number | null;
  retentionDeductions?: number | null;
  explicitDapDailyRate?: number | null;
  /** Tenant-local agreement date for legacy callers without an explicit catalogue rate. */
  priceAgreementDate?: string | null;
  mpirPct?: number | null;
}

export interface AccommodationAgreementBreakdown {
  paymentType: ResidentialAccommodationPaymentType;
  agreedRoomPrice: number;
  radPaid: number;
  unpaidRoomPrice: number;
  mpirPct: number;
  dapDailyRate: number;
}

export interface ResidentialRefundBreakdownInput {
  refundableBalance: number;
  /** Gross deduction ledger. Already-applied amounts are passed separately. */
  deductionTotal?: number | null;
  /** Sum of deductions_total already stored on earlier refund events. */
  priorAppliedDeductions?: number | null;
  /** Interest for this window only. Earlier interest is not added again. */
  interestAccrued?: number | null;
}

export interface ResidentialRefundBreakdown {
  refundableBalance: number;
  deductionTotal: number;
  interestAccrued: number;
  netRefundable: number;
}

export interface ResidentialRefundInterestDaysInput {
  /** Departure date. The first refund's interest window starts the next day. */
  departureDate?: string | null;
  refundDeadline: string;
  /** Latest earlier refund. The next window starts the day after it. */
  previousRefundDate?: string | null;
  refundDate: string;
}

export interface ResidentialRefundInterestDays {
  /** First day of this slice. Days are counted from here. */
  interestWindowStart: string;
  /** First day of the refund period. BIR is read on this day for every slice. */
  birRateDate: string;
  birDays: number;
  /**
   * Day after the original refund deadline. MPIR is read on this day
   * (Aged Care Rules r 313-10(4)), including when a later partial starts after it.
   */
  mpirRateDate: string;
  /** First counted MPIR day: the later of the interest window and mpirRateDate. */
  mpirDayStart: string;
  mpirDays: number;
}

export const RESIDENTIAL_MPIR_NOT_LOADED_MESSAGE = 'MPIR for that date is not loaded yet';

function agreementMpirPct(input: DailyAccommodationPaymentInput): number {
  if (input.mpirPct != null) return requireMpirPct(input.mpirPct);
  if (!input.priceAgreementDate) return requireMpirPct(input.mpirPct);
  try {
    return getResidentialMpirAnnualRateDecimal(input.priceAgreementDate) * 100;
  } catch {
    throw new Error(RESIDENTIAL_MPIR_NOT_LOADED_MESSAGE);
  }
}

/**
 * MPIR (percent) for a room price recorded alongside an agreement. A full RAD
 * charges no DAP, so its rate only sets the room's equivalent daily DAP: use
 * the agreement date's MPIR when it is loaded, otherwise the current MPIR.
 */
export function residentialRoomPriceMpirPct(priceAgreementDate?: string | null): number {
  if (!priceAgreementDate) return Number(MPIR_PCT);
  try {
    return getResidentialMpirAnnualRateDecimal(priceAgreementDate) * 100;
  } catch {
    return Number(MPIR_PCT);
  }
}

/**
 * Aged Care Rules r 296-5(3): multiply the unpaid amount by the percentage-point
 * rate, divide by 365, then one half-up round to cents. No intermediate round.
 * Integer arithmetic keeps the half-cent boundary exact.
 */
export function statutoryDapDaily(unpaidDollars: number, mpirPct: number): number {
  const unpaidCents = Math.round(Math.max(0, unpaidDollars) * 100);
  const mpirUnits = Math.round(mpirPct * 10000);
  const numerator = BigInt(unpaidCents) * BigInt(mpirUnits);
  const denominator = 365_000_000n;
  const cents = numerator / denominator;
  const remainder = numerator % denominator;
  const rounded = remainder * 2n >= denominator ? cents + 1n : cents;
  return Number(rounded) / 100;
}

function requireMpirPct(value: number | null | undefined): number {
  const parsed = Number(value);
  if (value == null || !Number.isFinite(parsed)) {
    throw new Error('accommodation_mpir_required');
  }
  return parsed;
}

export function calculateDailyAccommodationPayment(input: DailyAccommodationPaymentInput): number {
  const agreedRoomPrice = Math.max(0, Number(input.agreedRoomPrice || 0));
  const radPaid = Math.max(0, Number(input.radPaid || 0));
  const retained = Math.max(0, Number(input.retentionDeductions || 0));
  if (agreedRoomPrice === 0 || radPaid + retained >= agreedRoomPrice) return 0;
  const mpirPct = agreementMpirPct(input);
  return statutoryDapDaily(Math.max(0, agreedRoomPrice - radPaid - retained), mpirPct);
}

export function deriveAccommodationAgreementBreakdown(
  input: AccommodationAgreementBreakdownInput,
): AccommodationAgreementBreakdown {
  const agreedRoomPrice = Math.max(0, Number(input.agreedRoomPrice || 0));
  const needsDaily = input.paymentType !== 'RAD';
  const mpirPct = needsDaily
    ? agreementMpirPct(input)
    : input.mpirPct == null || !Number.isFinite(Number(input.mpirPct))
      ? 0
      : Number(input.mpirPct);

  const radPaid =
    input.paymentType === 'RAD'
      ? agreedRoomPrice
      : input.paymentType === 'COMBINATION'
        ? Math.max(0, Number(input.combinationRadPortion || 0))
        : 0;

  const unpaidRoomPrice = Math.max(0, agreedRoomPrice - radPaid);
  const dapDailyRate =
    input.paymentType === 'RAD'
      ? 0
      : input.explicitDapDailyRate != null
        ? roundMoney(Number(input.explicitDapDailyRate))
        : calculateDailyAccommodationPayment({
            agreedRoomPrice,
            radPaid,
            retentionDeductions: input.retentionDeductions,
            mpirPct,
          });

  return {
    paymentType: input.paymentType,
    agreedRoomPrice,
    radPaid,
    unpaidRoomPrice,
    mpirPct,
    dapDailyRate,
  };
}

const REFUND_DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

function refundDateKey(value: string): string {
  const key = value.trim().slice(0, 10);
  if (!REFUND_DATE_KEY.test(key)) {
    throw new Error(`residential_refund_interest_date:${value}`);
  }
  return key;
}

function refundUtcMidnight(date: string): number {
  const key = refundDateKey(date);
  const year = Number(key.slice(0, 4));
  const month = Number(key.slice(5, 7));
  const day = Number(key.slice(8, 10));
  return Date.UTC(year, month - 1, day);
}

function addRefundCalendarDays(date: string, days: number): string {
  const value = new Date(refundUtcMidnight(date));
  value.setUTCDate(value.getUTCDate() + days);
  const year = value.getUTCFullYear();
  const month = String(value.getUTCMonth() + 1).padStart(2, '0');
  const day = String(value.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function refundCalendarDaysBetween(startDate: string, endDate: string): number {
  return Math.round((refundUtcMidnight(endDate) - refundUtcMidnight(startDate)) / 86_400_000);
}

function moneyCents(value: number): number {
  return Math.round(value * 100);
}

export function calculateResidentialRefundBreakdown(
  input: ResidentialRefundBreakdownInput,
): ResidentialRefundBreakdown {
  const refundableCents = Math.max(0, moneyCents(Number(input.refundableBalance || 0)));
  const ledgerCents = Math.max(0, moneyCents(Number(input.deductionTotal || 0)));
  const priorAppliedCents = Math.max(0, moneyCents(Number(input.priorAppliedDeductions || 0)));
  const interestCents = Math.max(0, moneyCents(Number(input.interestAccrued || 0)));
  const unappliedCents = Math.max(0, ledgerCents - priorAppliedCents);

  return {
    refundableBalance: refundableCents / 100,
    deductionTotal: unappliedCents / 100,
    interestAccrued: interestCents / 100,
    netRefundable: Math.max(0, refundableCents - unappliedCents + interestCents) / 100,
  };
}

/**
 * Day split for one accommodation refund event. MPIR days start at the later
 * of the interest window and the day after the refund deadline, so a second
 * partial does not pay MPIR days the earlier refund already covered. The MPIR
 * rate date stays the day after the original refund period. The BIR rate
 * date is the first day of the refund period (Rules r 313-10(3)) for every
 * partial, not the start of this window (final review FR-09).
 */
export function residentialRefundInterestDays(
  input: ResidentialRefundInterestDaysInput,
): ResidentialRefundInterestDays {
  const refundDeadline = refundDateKey(input.refundDeadline);
  const refundDate = refundDateKey(input.refundDate);
  const refundPeriodStart = input.departureDate
    ? addRefundCalendarDays(input.departureDate, 1)
    : addRefundCalendarDays(refundDeadline, -13);
  let interestWindowStart = refundPeriodStart;
  if (input.previousRefundDate) {
    const dayAfterPreviousRefund = addRefundCalendarDays(input.previousRefundDate, 1);
    if (dayAfterPreviousRefund > interestWindowStart) {
      interestWindowStart = dayAfterPreviousRefund;
    }
  }

  const mpirRateDate = addRefundCalendarDays(refundDeadline, 1);
  const mpirDayStart = interestWindowStart > mpirRateDate ? interestWindowStart : mpirRateDate;
  let birDays = 0;
  let mpirDays = 0;
  if (refundDate >= interestWindowStart) {
    const birEnd = refundDate < refundDeadline ? refundDate : refundDeadline;
    birDays = Math.max(0, refundCalendarDaysBetween(interestWindowStart, birEnd) + 1);
  }
  if (refundDate >= mpirDayStart) {
    mpirDays = Math.max(0, refundCalendarDaysBetween(mpirDayStart, refundDate) + 1);
  }

  return {
    interestWindowStart,
    birRateDate: refundPeriodStart,
    birDays,
    mpirRateDate,
    mpirDayStart,
    mpirDays,
  };
}

/** Accrual is the sum of prescribed daily cents, never rounding the run's raw rate. */
export function calculateAccommodationDapAccrual(dailyRate: number, days: number): number {
  if (!Number.isInteger(days) || days < 0) throw new RangeError('Invalid DAP day count');
  return (moneyToCents(dailyRate) * days) / 100;
}

/** Tenant-local YYYY-MM-DD dates. Section 300 permits the cessation day.
 * Paul, 14 Oct 2026: a same-day transfer is charged by the receiving home only.
 */
export function isAccommodationDapDayChargeable(input: {
  date: string;
  startDate: string;
  departureDate?: string | null;
  sameDayTransferDate?: string | null;
  transfers?: Array<{ transferDate: string; returnDate: string | null }>;
}): boolean {
  return (
    input.date >= input.startDate &&
    (!input.departureDate || input.date <= input.departureDate) &&
    (!input.sameDayTransferDate || input.date < input.sameDayTransferDate) &&
    !(input.transfers ?? []).some(
      ({ transferDate, returnDate }) =>
        input.date >= transferDate && (!returnDate || input.date < returnDate),
    )
  );
}
